import React, { useEffect, useMemo, useState } from "react";
import { ObjectId } from "bson";
import { teachersQuestionAnswerGeneration } from "../../../services/operations/chatbotAPI";
import { useDispatch, useSelector } from "react-redux";
import { fetchCurriculumByClass } from "../../../services/operations/curriculumAPI";
import { BookOpen, List } from "lucide-react";
import ReactMarkdown from "react-markdown";
import "katex/dist/katex.min.css";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import { FileText, FileDown, Edit3, X, Check } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function QuetionPaper() {
  const { t } = useTranslation();
  const user = useSelector((state) => state.profile?.user);
  const dispatch = useDispatch();

  //-----------------------------------select sub and chapter-----------------------------
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedChapter, setSelectedChapter] = useState("");
  const [curriculumData, setCurriculumData] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);

  // Fetch curriculum data
  useEffect(() => {
    const loadCurriculum = async () => {
      if (!user?.class) return;
      const curriculumData = await dispatch(fetchCurriculumByClass());
      if (curriculumData) {
        setCurriculumData(curriculumData);
        if (curriculumData.subjects) {
          const subjectList = Object.keys(curriculumData.subjects).map(
            (key) => ({
              id: key,
              name:
                key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " "),
            })
          );
          setSubjects(subjectList);
        }
      }
    };
    loadCurriculum();
  }, [user?.class, dispatch]);

  useEffect(() => {
    if (
      selectedSubject &&
      curriculumData?.subjects?.[selectedSubject]?.chapters
    ) {
      setChapters(curriculumData.subjects[selectedSubject].chapters);
    } else {
      setChapters([]);
    }
  }, [selectedSubject, curriculumData]);

  //--------------------------------------------------------------------------------------------------------------

  const [messages, setMessages] = useState([
    { role: "assistant", content: t("questionPaper.greeting") },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editedText, setEditedText] = useState("");

  const handleSend = async () => {
    if (!input.trim()) return;

    // Add user message immediately
    const userMessage = { role: "user", content: input };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    try {
      const conversationId = new ObjectId().toHexString();

      const response = await teachersQuestionAnswerGeneration({
        query: input,
        conversationId: conversationId,
        className: curriculumData?._id,
        subjectName: selectedSubject,
        chapterName: selectedChapter,
      });

      // console.log("res",response?.response)

      const botReply = response?.response || "";
      const cleanedResponse = botReply.replace(/^\$\$|\$\$$/g, "").trim();
      const assistantContent =
        cleanedResponse || "Sorry, I could not understand.";

      const assistantMessage = {
        role: "assistant",
        content: assistantContent,
        webLinks: response?.webLinks || [],
        youtubeLinks: response?.youtubeLinks || [],
      };

      // console.log("res",assistantContent)
      setMessages((prev) => [...prev, assistantMessage]);
    } catch (err) {
      console.error("Error in handleSend:", err);
      const errorMessage = {
        role: "assistant",
        content: "⚠️ Something went wrong. Please try again.",
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const preprocessContent = (content) => {
    if (!content) return "";

    let processed = content;

    processed = processed.replace(
      /([.!?]\s*)([📐🧮🔍🧩⚠️🔑✏️📝💡🔢👀🔗❗📏⚡])/g,
      "$1\n\n$2"
    );
    processed = processed.replace(
      /(\S)(\s*)([📐🧮🔍🧩⚠️🔑✏️📝💡🔢👀🔗❗📏⚡])/g,
      "$1\n\n$3"
    );
    processed = processed.replace(
      /^([^#\n]*?)([📐🧮🔍🧩⚠️🔑✏️📝💡🔢👀🔗❗📏⚡])/gm,
      "$1\n\n$2"
    );
    processed = processed.replace(/#{4,}/g, "\n\n");
    processed = processed.replace(
      /^(?:new_query|continuous_query)+\s*###\s*(.*)/i,
      "# $1"
    );
    processed = processed.replace(/^(?:continuous_query|new_query|#)+\s*/i, "");
    processed = processed.replace(/([.?!])\s*(#{1,6}\s)/g, "$1\n\n$2");
    processed = processed.replace(
      /([^\n])\n(^\s*([*+-]|\d+\.)\s)/gm,
      "$1\n\n$2"
    );
    processed = processed.replace(/^(#{1,6})([^\s#])/gm, "$1 $2");
    processed = processed.replace(/^(\s*[*+-])([^\s])/gm, "$1 $2");
    processed = processed.replace(/^(\s*\d+\.)([^\s])/gm, "$1 $2");
    processed = processed.replace(/(#{1,6})\s*[*]+\s*/g, "$1 ");
    processed = processed.replace(/\*\*\s*(.*?)\s*\*\*/g, "**$1**");
    processed = processed.replace(/(?<!\*)\*\s+(.*?)\s+\*(?!\*)/g, "*$1*");
    processed = processed.replace(/^(\s*)\d+\.\s/gm, "$1⭐ ");
    processed = processed.replace(/\)\*\*(?=[A-Z][a-z])/g, ")\n\n");
    processed = processed.replace(/\n{3,}/g, "\n\n");
    processed = processed.replace(/([#-])\1{3,}/g, "\n\n");
    processed = processed.replace(/([a-z])([A-Z])/g, "$1 $2");
    processed = processed.replace(/([a-zA-Z])-([A-Z])/g, "$1: $2");

    return processed.trim();
  };

  const fixMathErrors = (content) => {
    if (!content) return "";

    let processed = content;

    processed = processed.replace(/\\\(([\s\S]*?)\\\)/g, "$$$1$");
    processed = processed.replace(/\\\[([\s\S]*?)\\\]/g, "$$$$$1$$$$");
    processed = processed.replace(
      /(?<!\\)(\$\$?)([\s\S]+?)\1/g,
      (match, delimiter, mathExpression) => {
        const singleLineExpression = mathExpression
          .replace(/\s*\n\s*/g, " ")
          .trim();
        return `${delimiter}${singleLineExpression}${delimiter}`;
      }
    );

    return processed;
  };

  const markdownComponents = useMemo(
    () => ({
      p: ({ children }) => (
        <p className="mb-4 leading-7 text-base">{children}</p>
      ),
      ul: ({ children }) => (
        <ul className="mb-6 mt-2 ml-6 space-y-2 list-disc list-outside">
          {children}
        </ul>
      ),
      ol: ({ children }) => (
        <ol className="mb-6 mt-2 ml-6 space-y-2 list-decimal list-outside">
          {children}
        </ol>
      ),
      li: ({ children }) => <li className="leading-7 pl-2">{children}</li>,
      h1: ({ children }) => (
        <h1 className="text-md font-bold mt-8 mb-6 pb-3 border-b-2 leading-tight">
          {children}
        </h1>
      ),
      h2: ({ children }) => (
        <h2 className="text-xl font-semibold mt-7 mb-4 leading-tight">
          {children}
        </h2>
      ),
      h3: ({ children }) => (
        <h3 className="text-xl font-semibold mt-6 mb-3 leading-tight">
          {children}
        </h3>
      ),
      h4: ({ children }) => (
        <h4 className="text-lg font-semibold mt-5 mb-2 leading-tight">
          {children}
        </h4>
      ),
      h5: ({ children }) => (
        <h5 className="text-base font-semibold mt-4 mb-2 leading-tight">
          {children}
        </h5>
      ),
      h6: ({ children }) => (
        <h6 className="text-sm font-medium mt-3 mb-1 leading-tight">
          {children}
        </h6>
      ),
      strong: ({ children }) => (
        <strong className="font-semibold">{children}</strong>
      ),
      em: ({ children }) => <em className="italic">{children}</em>,
      a: ({ href, children }) => (
        <a
          href={href}
          className="underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          {children}
        </a>
      ),
      code: ({ node, inline, className, children, ...props }) => (
        <code
          className="bg-gray-200 text-gray-800 px-1 py-0.5 rounded-md"
          {...props}
        >
          {children}
        </code>
      ),
    }),
    []
  );

  // Utility: Clean markdown-like syntax
  const cleanMarkdown = (text) => {
    return text
      .replace(/^#{1,6}\s*/gm, "") // remove #, ##, ###
      .replace(/\*\*(.*?)\*\*/g, "$1") // bold
      .replace(/\*(.*?)\*/g, "$1") // italic
      .replace(/`(.*?)`/g, "$1") // inline code
      .replace(/>\s?/g, "") // blockquotes
      .replace(/!\[.*?\]\(.*?\)/g, "") // remove images
      .replace(/\[([^\]]+)\]\((.*?)\)/g, "$1") // keep link text only
      .trim();
  };

  const handleDownloadPDF = async (content) => {
    try {
      const cleaned = cleanMarkdown(content);

      const response = await fetch(
        `${process.env.REACT_APP_VOICE_TO_VOICE_URL}/generate-pdf`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ text: cleaned }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to generate PDF");
      }

      // Get PDF blob from backend
      const blob = await response.blob();

      // Create a downloadable link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "response.pdf";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("PDF generation error:", error);
      alert("Failed to generate PDF. Please try again.");
    }
  };

  const handleDownloadDocx = async (content) => {
    try {
      const cleaned = cleanMarkdown(content);

      const response = await fetch(
        `${process.env.REACT_APP_VOICE_TO_VOICE_URL}/generate-docx`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ text: cleaned }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to generate DOCX");
      }

      // Get DOCX blob from backend
      const blob = await response.blob();

      // Create a downloadable link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "response.docx";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("DOCX generation error:", error);
      alert("Failed to generate DOCX. Please try again.");
    }
  };

  const handleEdit = (msg, index) => {
    setEditingIndex(index);
    setEditedText(msg);
  };

  // 💾 Save edited message
  const handleSaveEdit = (index) => {
    const updated = [...messages];
    updated[index].content = editedText;
    setMessages(updated);
    setEditingIndex(null);
  };

  // ❌ Cancel editing
  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditedText("");
  };

  return (
    <div className="flex flex-col h-[90vh] w-full bg-white text-gray-800">
      {/* Subject and Chapter Dropdowns */}
      <div className="p-4 border-b border-gray-200 bg-gray-200 text-gray-800 grid grid-cols-1 sm:grid-cols-2 gap-2">
        {/* Subject Dropdown */}
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-gray-400" />
          <select
            value={selectedSubject}
            onChange={(e) => {
              setSelectedSubject(e.target.value);
              setSelectedChapter("");
            }}
            className="flex-1 p-2 rounded-md bg-white text-gray-800 border border-gray-300 text-sm focus:border-blue-500 outline-none"
            disabled={subjects.length === 0}
          >
            <option value="">
          {subjects.length === 0
                ? t("questionPaper.loadingSubjects")
                : t("questionPaper.selectSubject")}
            </option>
            {subjects.map((subject) => (
              <option key={subject.id} value={subject.id}>
                {subject.name}
              </option>
            ))}
          </select>
        </div>

        {/* Chapter Dropdown */}
        <div className="flex items-center gap-2">
          <List className="w-4 h-4 text-gray-400" />
          <select
            value={selectedChapter}
            onChange={(e) => setSelectedChapter(e.target.value)}
            disabled={!selectedSubject || chapters.length === 0}
            className="flex-1 p-2 rounded-md bg-white text-gray-800 border border-gray-300 text-sm focus:border-blue-500 outline-none disabled:opacity-50 truncate"
            title={selectedChapter}
          >
            <option value="">
              {!selectedSubject
                ? t("questionPaper.selectSubjectFirst")
                : chapters.length === 0
                ? t("questionPaper.noChapters")
                : t("questionPaper.selectChapter")}
            </option>
            {chapters.map((chapter, idx) => (
              <option key={idx} value={chapter}>
                {chapter.length > 50 ? chapter.slice(0, 50) + "..." : chapter}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Chat messages */}
      <div className="flex-1 overflow-y-auto p-6 bg-slate-10 space-y-3">
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex ${
              msg.role === "user" ? "justify-end" : "justify-start"
            }`}
          >
            <div
              className={`max-w-[75%] px-4 py-2 rounded-2xl shadow-sm ${
                msg.role === "user"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-300 text-gray-800"
              }`}
            >
              {/* If editing this message */}
              {editingIndex === idx ? (
                <div className="bg-white rounded-xl p-4 shadow-lg border border-gray-300">
                  <textarea
                    className=" min-w-[800px] min-h-[300px] max-h-[800px] p-3 rounded-lg border border-gray-400 focus:ring-2 focus:ring-blue-500 focus:outline-none resize-y text-gray-900"
                    value={editedText}
                    onChange={(e) => setEditedText(e.target.value)}
                       placeholder={t("questionPaper.editPlaceholder")}
                  />
                  <div className="flex gap-4 mt-3 justify-end">
                    <button
                      onClick={() => handleSaveEdit(idx)}
                      className="flex items-center gap-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                    >
                      <Check size={18} /> {t("questionPaper.save")}
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="flex items-center gap-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
                    >
                      <X size={18} />  {t("questionPaper.cancel")}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Normal markdown view */}
                  {msg.content && msg.content.trim() && (
                    <ReactMarkdown
                      remarkPlugins={[remarkMath]}
                      rehypePlugins={[rehypeKatex]}
                      components={markdownComponents}
                    >
                      {fixMathErrors(preprocessContent(msg.content))}
                    </ReactMarkdown>
                  )}

                  {/* AI controls */}
                  {msg.role !== "user" && (
                    <div className="flex items-center gap-3 mt-2 text-gray-600">
                      <button
                        onClick={() => handleEdit(msg.content, idx)}
                        title={t("questionPaper.edit")}
                        className="hover:text-blue-600"
                      >
                        <Edit3 size={18} />
                      </button>
                      <button
                        onClick={() => handleDownloadPDF(msg.content)}
                        title={t("questionPaper.pdf")}
                        className="hover:text-red-500"
                      >
                        <FileDown size={18} />
                      </button>
                      <button
                        onClick={() => handleDownloadDocx(msg.content)}
                        title={t("questionPaper.docx")}
                        className="hover:text-green-600"
                      >
                        <FileText size={18} />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        ))}

        {/* Loader */}
        {isLoading && (
          <div className="flex items-center space-x-2">
            <div className="flex space-x-1">
              <span className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce"></span>
              <span
                className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce"
                style={{ animationDelay: "0.15s" }}
              ></span>
              <span
                className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce"
                style={{ animationDelay: "0.3s" }}
              ></span>
            </div>
            <span className="text-sm text-gray-400 font-medium">
             <span>{t("questionPaper.aiThinking")}</span>
            </span>
          </div>
        )}
      </div>

      {/* Input area */}
      <div className="flex items-center gap-2 p-4 border-t border-gray-200 bg-gray-200 text-gray-800">
        <input
          type="text"
          placeholder="Type your message..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSend()}
          className="flex-1 px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          onClick={handleSend}
          disabled={isLoading}
          className={`${
            isLoading ? "bg-gray-400" : "bg-blue-600 hover:bg-blue-700"
          } text-white font-medium px-4 py-2 rounded-lg transition`}
        >
          {isLoading ? t("questionPaper.loading") : t("questionPaper.send")}
        </button>
      </div>
    </div>
  );
}
